'use client';
import { Fragment } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

const BlogHero = ({ type = 'Blog', title, author, date, readTime, image }) => {
    const pathname = usePathname();

    return (
        <Fragment>
            <div className="w-full py-8 md:py-12 backdrop-blur-3xl shadow-inner bg-gradient-to-r from-deep-purple-400/20 via-blue-400/20 to-pink-500/20">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8">
                        {/* Image Section */}
                        <div className="w-full md:w-1/2 overflow-hidden">
                            <Image
                                src={image}
                                height={500}
                                width={500}
                                alt="img"
                                className="w-full rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
                            />
                        </div>

                        {/* Content Section */}
                        <div className="w-full md:w-1/2">
                            <div id="text" className="space-y-4">
                                {/* Type Badge */}
                                <p className="uppercase text-sm font-extrabold text-blue-500">
                                    {type}
                                </p>

                                {/* Title */}
                                <h1 className="text-2xl md:text-4xl font-bold text-[#2e2e2e] hover:text-blue-600 transition-colors duration-300">
                                    {title}
                                </h1>

                                {/* Meta Information */}
                                <div id="name" className="space-y-4 py-4">
                                    {/* Author */}
                                    <div className="flex gap-3 items-center">
                                        <Image
                                            src={`${process.env.NEXT_PUBLIC_API_ENDPOINT}${author.image}`}
                                            height={40}
                                            width={40}
                                            alt="img"
                                            className="rounded-full ring-2 ring-gray-200 hover:ring-blue-400 transition-all duration-300"
                                        />
                                        <div className="leading-tight">
                                            <span className="font-semibold text-base hover:text-blue-600 transition-colors duration-300 cursor-pointer">
                                                {author.name}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Date and Read Time */}
                                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                                        <div className="flex items-center gap-2">
                                            <i className="ri-calendar-line text-gray-400"></i>
                                            <span>
                                                {`${new Date(
                                                    date,
                                                ).toLocaleString('default', {
                                                    month: 'long',
                                                })} ${new Date(
                                                    date,
                                                ).getDate()}, ${new Date(
                                                    date,
                                                ).getFullYear()}`}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <i className="ri-time-line text-gray-400"></i>
                                            <span>{readTime} min read</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Social Share Buttons */}
                                <div className="pt-2">
                                    <p className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wide">
                                        Share this article
                                    </p>
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <Link
                                            href={`https://twitter.com/intent/tweet?url=https://wahni.io${pathname}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <i className="ri-twitter-x-fill bg-white rounded-full p-2.5 text-lg md:text-xl text-black hover:bg-gray-100 hover:scale-110 hover:-translate-y-1 transition-all duration-200 hover:shadow-xl cursor-pointer shadow-md"></i>
                                        </Link>
                                        <Link
                                            href={`https://wa.me/?text=https://wahni.io${pathname}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <i className="ri-whatsapp-fill bg-white rounded-full p-2.5 text-lg md:text-xl text-green-500 hover:bg-green-50 hover:scale-110 hover:-translate-y-1 transition-all duration-200 hover:shadow-xl cursor-pointer shadow-md"></i>
                                        </Link>
                                        <Link
                                            href={`https://www.linkedin.com/shareArticle?mini=true&url=https://wahni.io${pathname}&title=${title}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <i className="ri-linkedin-box-fill bg-white rounded-full p-2.5 text-lg md:text-xl text-blue-600 hover:bg-blue-50 hover:scale-110 hover:-translate-y-1 transition-all duration-200 hover:shadow-xl cursor-pointer shadow-md"></i>
                                        </Link>
                                        <Link
                                            href={`https://www.facebook.com/sharer/sharer.php?u=https://wahni.io${pathname}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <i className="ri-facebook-circle-fill bg-white rounded-full p-2.5 text-xl md:text-2xl text-blue-500 hover:bg-blue-50 hover:scale-110 hover:-translate-y-1 transition-all duration-200 hover:shadow-xl cursor-pointer shadow-md"></i>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default BlogHero;
